import { matchPath } from "react-router-dom";
import { EVENT_NAMES, PAGE_TYPES, PARAM_KEYS } from "./constants";

function normalizeRouteState(location) {
  if (!location || typeof location.state !== "object" || location.state == null) {
    return {};
  }

  return location.state;
}

function normalizeZip(value) {
  const normalizedValue = String(value || "").replace(/\D/g, "").slice(0, 5);
  return /^\d{5}$/.test(normalizedValue) ? normalizedValue : undefined;
}

export function deriveAnalyticsRouteContext(location) {
  const routeState = normalizeRouteState(location);
  const pathname = location.pathname;

  const surveyCompleteMatch = matchPath({ path: "/survey/:slug/complete", end: true }, pathname);

  if (surveyCompleteMatch) {
    return {
      pageType: PAGE_TYPES.SURVEY_COMPLETE,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.SURVEY_COMPLETE,
        [PARAM_KEYS.CATEGORY_KEY]: surveyCompleteMatch.params.slug,
        [PARAM_KEYS.ZIP_CODE]: normalizeZip(routeState.zip),
        [PARAM_KEYS.PROJECT_GUID]: routeState.projectGuid,
      },
    };
  }

  const surveyMatch = matchPath({ path: "/survey/:slug", end: true }, pathname);

  if (surveyMatch) {
    return {
      pageType: PAGE_TYPES.SURVEY,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.SURVEY,
        [PARAM_KEYS.CATEGORY_KEY]: surveyMatch.params.slug,
        [PARAM_KEYS.ZIP_CODE]: normalizeZip(routeState.zip),
      },
    };
  }

  const reportMatch = matchPath({ path: "/report/:customerId", end: true }, pathname);

  if (reportMatch) {
    return {
      pageType: PAGE_TYPES.REPORT,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.REPORT,
        [PARAM_KEYS.CATEGORY_KEY]: routeState.categoryKey,
        [PARAM_KEYS.PROJECT_GUID]: routeState.projectGuid,
      },
    };
  }

  if (matchPath({ path: "/providers", end: true }, pathname)) {
    return {
      pageType: PAGE_TYPES.PROVIDER_LIST,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.PROVIDER_LIST,
      },
    };
  }

  const providerProfileMatch = matchPath({ path: "/providers/:id", end: true }, pathname);

  if (providerProfileMatch) {
    return {
      pageType: PAGE_TYPES.PROVIDER_PROFILE,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.PROVIDER_PROFILE,
        [PARAM_KEYS.PROVIDER_ID]: providerProfileMatch.params.id,
      },
    };
  }

  if (matchPath({ path: "/california-hidden-tax-fee-transparency-petition", end: true }, pathname)) {
    return {
      pageType: PAGE_TYPES.CAMPAIGN,
      viewEventName: EVENT_NAMES.CAMPAIGN_VISIT,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.CAMPAIGN,
        [PARAM_KEYS.CAMPAIGN_ID]: "california-hidden-tax-fee-transparency-petition",
      },
    };
  }

  if (matchPath({ path: "/faqs", end: true }, pathname)) {
    return {
      pageType: PAGE_TYPES.FAQ,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.FAQ,
      },
    };
  }

  if (pathname.startsWith("/tools/")) {
    return {
      pageType: PAGE_TYPES.TOOL,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.TOOL,
        [PARAM_KEYS.CATEGORY_KEY]: routeState.categoryKey || routeState.projectType,
      },
    };
  }

  if (
    pathname === "/terms" ||
    pathname === "/privacy" ||
    pathname === "/do-not-sell" ||
    pathname === "/privacy/confirmed"
  ) {
    return {
      pageType: PAGE_TYPES.LEGAL,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.LEGAL,
      },
    };
  }

  const categoryLandingMatch = matchPath({ path: "/in-town/:category", end: true }, pathname);

  if (categoryLandingMatch) {
    return {
      pageType: PAGE_TYPES.CATEGORY_LANDING,
      viewEventName: EVENT_NAMES.VIEW_CATEGORY_LANDING,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.CATEGORY_LANDING,
        [PARAM_KEYS.CATEGORY_KEY]: categoryLandingMatch.params.category,
      },
    };
  }

  const zipLandingMatch = matchPath({ path: "/:zip/:category", end: true }, pathname);

  if (zipLandingMatch) {
    const categoryKey = zipLandingMatch.params.category;
    const zipCode = normalizeZip(zipLandingMatch.params.zip);

    if (zipCode) {
      return {
        pageType: PAGE_TYPES.ZIP_LANDING,
        viewEventName: EVENT_NAMES.VIEW_ZIP_LANDING,
        context: {
          [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.ZIP_LANDING,
          [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
          [PARAM_KEYS.ZIP_CODE]: zipCode,
        },
      };
    }
  }

  if (pathname === "/") {
    return {
      pageType: PAGE_TYPES.HOME,
      viewEventName: EVENT_NAMES.VIEW_HOME,
      context: {
        [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.HOME,
      },
    };
  }

  return {
    pageType: PAGE_TYPES.OTHER,
    context: {
      [PARAM_KEYS.PAGE_TYPE]: PAGE_TYPES.OTHER,
    },
  };
}
